import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';

/**
 * SearchPopup Component
 * A Notion-style popup with search field for campaigns, templates, or trash
 * Uses a portal to render at document body level to avoid z-index issues
 */
const SearchPopup = ({
  isOpen,
  onClose,
  title,
  icon,
  items = [],
  renderItem,
  emptyMessage = 'No items',
  searchPlaceholder = 'Search...',
  footerContent,
  extraContent = null,
  position = { top: 0, left: 0 },
  variant = 'default', // 'default', 'trash', 'template'
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [adjustedPosition, setAdjustedPosition] = useState(position);
  const popupRef = useRef(null);
  const inputRef = useRef(null);

  // Filter items — trash holds campaigns, templates, and docs (match name/title/tags)
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items;
    const query = searchQuery.toLowerCase();
    return items.filter((item) => {
      const parts = [item.name, item.title];
      if (Array.isArray(item.tags)) parts.push(...item.tags);
      return parts
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query);
    });
  }, [items, searchQuery]);

  // Adjust position to keep popup within viewport
  useEffect(() => {
    if (!isOpen) return;
    
    // Wait for popup to render to get its actual dimensions
    const adjustPosition = () => {
      if (!popupRef.current) return;
      
      const popup = popupRef.current;
      const popupRect = popup.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const padding = 16; // Minimum distance from viewport edges
      
      let newTop = position.top;
      let newLeft = position.left;
      
      // Check if popup extends below viewport
      if (position.top + popupRect.height > viewportHeight - padding) {
        // Position popup so its bottom is at the viewport edge minus padding
        newTop = Math.max(padding, viewportHeight - popupRect.height - padding);
      }
      
      // Check if popup extends beyond right edge
      if (position.left + popupRect.width > viewportWidth - padding) {
        newLeft = Math.max(padding, viewportWidth - popupRect.width - padding);
      }
      
      setAdjustedPosition({ top: newTop, left: newLeft });
    };
    
    // Use requestAnimationFrame to ensure popup is rendered
    requestAnimationFrame(adjustPosition);
  }, [isOpen, position]);

  // Focus input when popup opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Small delay to ensure popup is rendered
      setTimeout(() => inputRef.current?.focus(), 50);
    }
    // Clear search when closing
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        onClose();
      }
    };
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Use portal to render at document body level
  return createPortal(
    <div 
      ref={popupRef}
      className={`search-popup ${variant !== 'default' ? `search-popup--${variant}` : ''}`}
      style={{
        position: 'fixed',
        top: adjustedPosition.top,
        left: adjustedPosition.left,
      }}
    >
      {/* Header */}
      <div className="search-popup__header">
        {icon && <span className="search-popup__icon">{icon}</span>}
        <span className="search-popup__title">{title}</span>
        <span className="search-popup__count">{items.length}</span>
      </div>

      {/* Search Input */}
      <div className="search-popup__search">
        <svg 
          className="search-popup__search-icon" 
          width="14" 
          height="14" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2"
        >
          <circle cx="11" cy="11" r="8"/>
          <path d="m21 21-4.35-4.35"/>
        </svg>
        <input
          ref={inputRef}
          type="text"
          className="search-popup__input"
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button 
            className="search-popup__clear"
            onClick={() => setSearchQuery('')}
          >
            ✕
          </button>
        )}
      </div>

      {typeof extraContent === 'function' ? extraContent(searchQuery) : extraContent}

      {/* Items List */}
      <div className="search-popup__list">
        {filteredItems.length > 0 ? (
          filteredItems.map(item => renderItem(item, onClose))
        ) : (
          <div className="search-popup__empty">
            {searchQuery ? `No results for "${searchQuery}"` : emptyMessage}
          </div>
        )}
      </div>

      {/* Footer */}
      {footerContent && (
        <div className="search-popup__footer">
          {footerContent}
        </div>
      )}
    </div>,
    document.body
  );
};

export default SearchPopup;

